/*
 * (c) Copyright 2024 Calabrio, Inc.
 * All Rights Reserved. www.calabrio.com LICENSED MATERIALS
 * Property of Calabrio, Inc., Minnesota, USA
 *
 * No part of this publication may be reproduced, stored or transmitted,
 * in any form or by any means (electronic, mechanical, photocopying,
 * recording or otherwise) without prior written permission from Calabrio, Inc.
 */
import InteractionsService from '../../service/interactions';
import WyUtils from '../../library/wy-utils.js';
import WyFilter from '../../library/wy-filter.js';
import moment from 'moment';

const state = {
  contactReasonRunStats: null,
  transcriptInfoReference: null,
};

const getters = {
  contactReasonRunStats: (state) => state.contactReasonRunStats,
  transcriptInfoReference: (state) => state.transcriptInfoReference,
};

const mutations = {
  updateTranscriptInfoReference(state, data) {
    if (data) {
      state.transcriptInfoReference = data;
    } else {
      state.transcriptInfoReference = null;
    }
  },
};

const actions = {
  fetchChatTopics({ getters }, options) {
    let payload = Object.assign(WyFilter.filterValues(getters.shareState, getters.dataModel), options.dateRanges);

    if (getters.dataModel.filters.topics && getters.dataModel.filters.topics.value) {
      payload[getters.dataModel.filters.topics.name] = getters.dataModel.filters.topics.value.reduce((list, item) => {
        item.mergedTopics.map((el) => list.push(el));
        return list;
      }, []);
    }
    payload.topics = []; //Always fetch date without topics filter
    if (options && options.maxTopics) payload.maxTopics = options.maxTopics;
    return InteractionsService.fetchChatTopics(getters.authUser, payload);
  },

  async fetchChatFilters({ getters, dispatch, rootState }, { pageName }) {
    return new Promise(async (resolve) => {
      const dataModel = rootState.site.pages[pageName].dataModel;

      if (!getters.localStorage.agentData) {
        dataModel.filters = {
          agentGroups: { label: 'Group', name: 'agentGroups', value: null, type: 'multiSelectCheckbox', placeholder: 'Search agent groups', category: 'moreFilters' },
          agentTeams: { label: 'Team', name: 'agentTeams', value: null, type: 'multiSelectCheckbox', placeholder: 'Search teams', category: 'moreFilters' },
          agents: { label: 'Agent', name: 'agents', value: null, type: 'multiSelectCheckbox', placeholder: 'Search agents', category: 'moreFilters' },
          topics: { label: 'Topics', name: 'topics', value: null, type: 'hidden' },
          directions: { label: 'Direction', name: 'directions', value: '', type: 'checkbox', isRequire: true }
        };

        let res = await InteractionsService.fetchChatFilters(getters.authUser);
        if (res && res.status == 200 && res.data) {
          await dispatch('updateLocalStorage', { key: 'agentData', data: {
            agentGroups: res.data.agentGroups,
            agentTeams: res.data.agentTeams,
            agents: res.data.agents
          }});

          // date limits
          if (res.data.dateLimits && res.data.dateLimits.length > 0) {
            dataModel.dateMin = res.data.dateLimits[0].firstDate;
            dataModel.dateMax = res.data.dateLimits[0].lastDate;
          }

          // agentGroups list
          if (dataModel.filters.agentGroups) {
            dataModel.filters.agentGroups.dataSource = (res.data.agentGroups || []).map((item) => {
              return { key: item.groupId, label: item.groupName };
            });
            dataModel.filters.agentGroups.dataSource.sort((a, b) => a.label.localeCompare(b.label))
            dataModel.filters.agentGroups.dataSource.unshift({ key: 'all', label: 'All' });
          }

          // agentTeams list
          if (dataModel.filters.agentTeams) {
            dataModel.filters.agentTeams.dataSource = (res.data.agentTeams || []).map((item) => {
              return { key: item.teamId, label: item.teamName, groupId: item.groupId };
            });
            dataModel.filters.agentTeams.dataSource.sort((a, b) => a.label.localeCompare(b.label))
            dataModel.filters.agentTeams.dataSource.unshift({ key: 'all', label: 'All' });
          }

          // agents list
          if (dataModel.filters.agents) {
            dataModel.filters.agents.dataSource = (res.data.agents || []).map((item) => {
              return { key: item.agentId, label: item.agentName, teamId: item.teamId, groupId: item.groupId };
            });
            dataModel.filters.agents.dataSource.sort((a, b) => a.label.localeCompare(b.label))
            dataModel.filters.agents.dataSource.unshift({ key: 'all', label: 'All' });
          }
          // direction list
          //mockdata: 
          res.data.directions = [
            {
              "direction": "inbound"
            },
            {
              "direction": "outbound"
            }
          ]
          if (dataModel.filters.directions) {
            const directionsMap = getters.authUser.config.app.configs.directionsMapping || {};
            // const directionsMap = getters.authUser.config.app.configs.directionsMapping || {'all': "All"};
            dataModel.filters.directions.dataSource = (res.data.directions || []).map((item) => {
              return { key: item.direction, label: directionsMap[item.direction] && directionsMap[item.direction] != '' ? directionsMap[item.direction] : WyUtils.toCamelCase(item.direction) };
            });
            dataModel.filters.directions.dataSource.unshift({ key: '', label: 'All' });
            dataModel.filters.directions.value = '';
          }

          // reset appliedFiltersCounter to 0
          dataModel.appliedFiltersCounter = 0;

          await dispatch('initFilterOriginalSource');
          resolve();
        } else {
          resolve();
        }
      } else {
        resolve();
      }
    });
  },

  fetchChatPerformance({ getters }, { dateRanges, topics }) {
    let payload = Object.assign(WyFilter.filterValues(getters.shareState, getters.dataModel), dateRanges);
    if (topics) {
      payload.topicDisplayNames = topics.map((item) => item.key);
      payload[getters.dataModel.filters.topics.name] = topics.reduce((list, item) => {
        item.mergedTopics.map((el) => list.push(el));
        return list;
      }, []);
    }
    return InteractionsService.fetchChatPerformance(getters.authUser, payload);
  },

  async fetchContactReasons({ commit, getters }, options) {
    if (getters.dataModel.dateStart && getters.dataModel.dateEnd) {
      let results = [];

      let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
      let res = await InteractionsService.fetchContactReasons(getters.authUser, options, payload);

      if (getters.moreFiltersChanged <= 0) {
        if (getters.dataModel.dateHasChanged) {
          getters.dataModel.dateHasChanged = false;
        }
      }

      // if not cached, process the following
      if (!(res === true)) {
        if (res && res.status === 200 && res.data.results) {
          let resultsA = res.data.results;
          //let resultsB = res.data.resultsCompare;
          let sumTotalVolume = resultsA.reduce((total, item) => {
            return total + item.totalVolume;
          }, 0);
          let resultsAObj = {};
          
          resultsA.forEach((item) => {
            resultsAObj[item.topicDisplayName] = item;
          });
          Object.keys(resultsAObj).forEach((key) => {
            let item = resultsAObj[key];
            item.isSelected = false;
            item.isEditing = false;
            item.isEditable = !item.isOthers;

            if (item.mergedTopics && item.mergedTopics.length > 1) {
              item.topicDisplayNameWithCount = item.topicDisplayName + ` (${item.mergedTopics.length})`; // showing number of merged topics
            } else {
              item.topicDisplayNameWithCount = item.topicDisplayName;
            }

            item.numOfDaysInRange = getters.dataModel.dateRangeInDays;
            item.volumePercent = ((item.totalVolume / sumTotalVolume) * 100).toFixed(2); // TODO: calculate the percent
            item.volumePercentNum = (item.totalVolume / sumTotalVolume) * 100;
            generateContactReasonsData(item, getters.xnumber);
            generatAlertIconData(item);

            results.push(item);
          });
          let totalTopics = 0;
          if (res.data.totalTopics) {
            totalTopics = res.data.totalTopics;
          }
          commit('updateTableModelResult', {
            results: results,
            totalRecs: results.length,
            totalNumber: totalTopics,
            totalPages: 1,
          });

          return true;
        }

        state.contactReasonRunStats = null;
        return false;
      } else {
        return true;
      }
    }
  },

  mergeInteractionContactReasonsTopics({ getters }, payload) {
    if (payload && payload.topicDisplayName) {
      return InteractionsService.mergeContactReasonsTopics(getters.authUser, payload);
    }
    return null;
  },

  unmergeContactReasons({ getters }, payload) {
    if (payload && payload.topicDisplayName && payload.topicsToUnmerge && payload.topicsToUnmerge.length > 0) {
      return InteractionsService.mergeContactReasonsTopics(getters.authUser, payload);
    }
    return null;
  },

  async updateTopicName({ getters }, topicNameObject) {
    if (topicNameObject && topicNameObject.newTopicDisplayName && topicNameObject.newTopicDisplayName.trim() != '') {
      return InteractionsService.updateTopicName(getters.authUser, topicNameObject);
    }
    return null;
  },

  fetchContactReasonsTopicTrend({ getters }, {item}) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    payload.topics = item.mergedTopics;
    payload.topicDisplayName = item.topicDisplayName;
    payload.isOthers = item.isOthers;

    return InteractionsService.fetchContactReasonsTopicTrend(getters.authUser, payload);
  },

  fetchContactReasonsTopicEntities({ getters }, data) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    payload.topics = data.topics;
    payload.topicDisplayName = data.topicDisplayName;

    return InteractionsService.fetchContactReasonsTopicEntities(getters.authUser, payload);
  },

  fetchTranscriptSearchTimeRange({ getters }, data) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    payload.topicDisplayName = data.topicDisplayName;
    payload.isOthers = data.isOthers;

    return InteractionsService.fetchTranscriptSearchTimeRange(getters.authUser, payload);
  },

  fetchTranscriptSummary({ getters }, data) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    payload.conversationId = data.conversationId;
    return InteractionsService.fetchTranscriptSummary(getters.authUser, payload);
  },

  async fetchTranscriptSearch({ getters }, data) {
    delete data.searchActivatedBy;

    const transcriptFilters = JSON.parse(JSON.stringify(data.transcriptSearchFilter));
    transcriptFilters.searchByType = "Empty";
    transcriptFilters.agentGroups = transcriptFilters.agentGroups || [];
    transcriptFilters.agentIds = transcriptFilters.agentIds || [];
    transcriptFilters.agentQueues = transcriptFilters.agentQueues || [];
    transcriptFilters.conversationTypes = transcriptFilters.conversationTypes || [];
    transcriptFilters.sentiment = transcriptFilters.sentiment || [];
    transcriptFilters.topicDisplayNames = transcriptFilters.topicDisplayNames || [];
    transcriptFilters.BES_filter_is_on = transcriptFilters.besSignals &&  transcriptFilters.besSignals.length > 0 || false,
    transcriptFilters.BAS_filter_is_on = transcriptFilters.basSignals  &&  transcriptFilters.basSignals.length > 0 || false,
    transcriptFilters.BES_signals = transcriptFilters.besSignals || [],
    transcriptFilters.BAS_signals  = transcriptFilters.basSignals || [],
    delete transcriptFilters.action;
    delete transcriptFilters.handOffsMax;
    delete transcriptFilters.handOffsMin;
    delete transcriptFilters.searchType;
    delete transcriptFilters.handleTimeMillisMax;
    delete transcriptFilters.handleTimeMillisMin;
    delete transcriptFilters.responseTimeMillisMax;
    delete transcriptFilters.responseTimeMillisMin;

    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    payload.topics = data.topics;
    // payload.transcriptFilters = data.transcriptFilters
    payload.messageId = data.messageId;
    if (data.transcriptSearchFilter && data.transcriptSearchFilter.searchType == '2') {
      if (data.transcriptSearchFilter && data.transcriptSearchFilter.searchKeyword && data.transcriptSearchFilter.searchKeyword != '') {
        let searchKeyword = data.transcriptSearchFilter.searchKeyword.trim();
        if (searchKeyword && searchKeyword != '') {
          let searchKeywordList = searchKeyword.split(',');
          payload.textQuery = searchKeywordList;
          transcriptFilters.searchByType = "Conversation ID Search";
        }
      }
    } 
    else {
      if (data.transcriptSearchFilter && data.transcriptSearchFilter.searchKeyword && data.transcriptSearchFilter.searchKeyword != '') {
        payload.textQuery = data.transcriptSearchFilter.searchKeyword;
        transcriptFilters.searchByType = "Keyword Search";
      }
    }
    if (data.sortBy) payload.sortBy = data.sortBy;
    if (data.pageNo) payload.pageNo = data.pageNo;
    if (data.pageSize) payload.pageSize = data.pageSize;
    // payload.savedTranscript = data.savedTranscript;
    if (data.isOthers != undefined) payload.isOthers = data.isOthers;

    payload.includeContactReasons = !data.contactReason || data.contactReason == '';
    payload.fromContactReasonsDetail = data.contactReason ? true : false;

    if (data.date) {
      payload.dateStart = data.date;
      payload.dateEnd = data.date;
    }
    payload.from = data.from;
    if (data.transcriptSearchFilter && Object.keys(data.transcriptSearchFilter).length > 0) {
      Object.keys(data.transcriptSearchFilter).forEach((key) => {
        payload[key] = data.transcriptSearchFilter[key];
      });
    }
    let res = await InteractionsService.fetchTranscriptSearch(getters.authUser, payload);

    if (res && res.status == 200 && res.data) {
      return res
    }
    else {
      return null
    }
  },

  async fetchTopicDisplayNameList({ getters }, data) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    payload.topics = data.topics;
    payload.messageId = data.messageId;
    if (data.transcriptSearchFilter && data.transcriptSearchFilter.searchType == '2') {
      if (data.transcriptSearchFilter && data.transcriptSearchFilter.searchKeyword && data.transcriptSearchFilter.searchKeyword != '') {
        let searchKeyword = data.transcriptSearchFilter.searchKeyword.trim();
        if (searchKeyword && searchKeyword != '') {
          let searchKeywordList = searchKeyword.split(',');
          payload.textQuery = searchKeywordList;
        }
      }
    } else {
      if (data.transcriptSearchFilter && data.transcriptSearchFilter.searchKeyword && data.transcriptSearchFilter.searchKeyword != '') payload.textQuery = data.transcriptSearchFilter.searchKeyword;
    }
    if (data.sortBy) payload.sortBy = data.sortBy;
    if (data.pageNo) payload.pageNo = data.pageNo;
    if (data.pageSize) payload.pageSize = data.pageSize;

    payload.includeContactReasons = !data.contactReason || data.contactReason == '';

    if (data.date) {
      payload.dateStart = data.date;
      payload.dateEnd = data.date;
    }
    payload.from = data.from;
    if (data.transcriptSearchFilter && Object.keys(data.transcriptSearchFilter).length > 0) {
      Object.keys(data.transcriptSearchFilter).forEach((key) => {
        payload[key] = data.transcriptSearchFilter[key];
      });
    }
    return InteractionsService.fetchTopicDisplayNameList(getters.authUser, payload);
  },


  exportTranscriptSearch({ getters }, data) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    payload.topics = data.topics;
    payload.messageId = data.messageId;
    if (data.transcriptSearchFilter && data.transcriptSearchFilter.searchType == '2') {
      if (data.transcriptSearchFilter && data.transcriptSearchFilter.searchKeyword && data.transcriptSearchFilter.searchKeyword != '') {
        let searchKeyword = data.transcriptSearchFilter.searchKeyword.trim();
        if (searchKeyword && searchKeyword != '') {
          let searchKeywordList = searchKeyword.split(',');
          payload.textQuery = searchKeywordList;
        }
      }
    } else {
      if (data.transcriptSearchFilter && data.transcriptSearchFilter.searchKeyword && data.transcriptSearchFilter.searchKeyword != '') payload.textQuery = data.transcriptSearchFilter.searchKeyword;
    }
    if (data.sortBy) payload.sortBy = data.sortBy;
    if (data.pageNo) payload.pageNo = data.pageNo;
    if (data.pageSize) payload.pageSize = data.pageSize;
    if (data.isOthers != undefined) payload.isOthers = data.isOthers

    if (data.date) {
      payload.dateStart = data.date;
      payload.dateEnd = data.date;
    }
    payload.from = data.from;
    if (data.transcriptSearchFilter && Object.keys(data.transcriptSearchFilter).length > 0) {
      Object.keys(data.transcriptSearchFilter).forEach((key) => {
        payload[key] = data.transcriptSearchFilter[key];
      });
    }

    return InteractionsService.exportTranscriptSearch(getters.authUser, payload);
  },

  addTranscript({ getters }, data) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    if (data.conversationId) payload.conversationId = data.conversationId;

    return InteractionsService.addTranscript(getters.authUser, payload);
  },

  removeTranscript({ getters }, data) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);

    if (data.conversationId) payload.conversationId = data.conversationId;
    return InteractionsService.removeTranscript(getters.authUser, payload);
  },

  fetchContactReasonsTopicConversations({ getters }, data) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    payload.topicDisplayName = data.topicDisplayName;
    payload.topics = data.topics;
    payload.messageId = data.messageId;
    if (data.from == 'relatedTopics') payload.topicDisplayName = data.topicDisplayName;
    if (data.textQuery && data.textQuery != '') payload.textQuery = data.textQuery;
    if (data.sortBy) payload.sortBy = data.sortBy;
    if (data.pageNo) payload.pageNo = data.pageNo;
    if (data.pageSize) payload.pageSize = data.pageSize;
    if (data.isOthers != null) payload.isOthers = data.isOthers;

    if (data.date) {
      payload.dateStart = data.date;
      payload.dateEnd = data.date;
    }
    payload.from = data.from;

    return InteractionsService.fetchContactReasonsTopicConversations(getters.authUser, payload);
  },

  fetchTopicsByConfidenceScore({ getters }, data) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);

    payload = {
      ...payload,
      topicDisplayName: data.topicDisplayName,
      messageId: data.messageId,
      topics: data.topics,
      from: data.from,
      ...(data.textQuery && data.textQuery != '' && { textQuery: data.textQuery }),
      ...(data.sortBy && { sortBy: data.sortBy }),
      ...(data.pageNo && { pageNo: data.pageNo }),
      ...(data.pageSize && { pageSize: data.pageSize }),
      ...(data.isOthers != null && { isOthers: data.isOthers }),
      ...(data.date && { dateStart: data.date, dateEnd: data.date }),
    }

    return InteractionsService.fetchTopicsByConfidenceScore(getters.authUser, payload);
  },

  fetchContactReasonsTopicConversationDetail({ getters }, payload) {
    return InteractionsService.fetchContactReasonsTopicConversationDetail(getters.authUser, payload);
  },

  fetchMetrics({ getters }) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);

    return InteractionsService.fetchMetrics(getters.authUser, payload);
  },
};

function convertToChangeValue(changeValue) {
  if (changeValue > 0) return '+';
  else if (changeValue < 0) return '-';
  else return '';
}

function generateContactReasonsData(item, xnumber) {
  let noneDataSorting = -1000000;

  // volume
  item.totalVolumeDMValue = item.totalVolume != null ? item.totalVolume * xnumber : item.totalVolume;
  if (item.totalVolumeCompare) {
    item.totalVolumeCompareDMValue = item.totalVolumeCompare != null ? item.totalVolumeCompare * xnumber : item.totalVolumeCompare;
    item.volumeChangeWithoutAbs = (item.totalVolume - item.totalVolumeCompare) / item.totalVolumeCompare;
    item.volumeChange = Math.abs(item.volumeChangeWithoutAbs);
    item.volumeChangeDisplay = convertToChangeValue(item.volumeChangeWithoutAbs) + (item.volumeChange * 100).toFixed(2);
    item.volumeChangePercent = (item.volumeChange * 100).toFixed(2);
    item.volumeChangeTooltip = generatTooltipsMsg(item.totalVolumeCompare != null ? item.totalVolumeCompare * xnumber : item.totalVolumeCompare, item.numOfDaysInRange, false);
  } else {
    item.volumeChange = null;
    item.volumeChangeWithoutAbs = noneDataSorting;
    item.volumeChangeDisplay = null;
  }

  // agent handle time
  item.avgAgentHandleTimeDisplay = item.avgAgentHandleTime >= 0 ? WyUtils.toNiceTimeFormat(item.avgAgentHandleTime * 60) : item.avgAgentHandleTime;
  if (item.avgAgentHandleTimeCompare && item.avgAgentHandleTimeCompare > -1) {
    item.avgAgentHandleTimeCompareDisplay = WyUtils.toNiceTimeFormat(item.avgAgentHandleTimeCompare * 60);
    item.avgAgentHandleTimeChangeWithoutAbs = (item.avgAgentHandleTime - item.avgAgentHandleTimeCompare) / item.avgAgentHandleTimeCompare;
    item.avgAgentHandleTimeChange = Math.abs(item.avgAgentHandleTimeChangeWithoutAbs);
    item.avgAgentHandleTimeChangeDisplay = convertToChangeValue(item.avgAgentHandleTimeChangeWithoutAbs) + (item.avgAgentHandleTimeChange * 100).toFixed(2);
    item.avgAgentHandleTimeChangePercent = (item.avgAgentHandleTimeChange * 100).toFixed(2);
    item.avgAgentHandleTimeChangeTooltip = generatTooltipsMsg(item.avgAgentHandleTimeCompareDisplay, item.numOfDaysInRange, false);
  } else {
    item.avgAgentHandleTimeCompareDisplay = null;
    item.avgAgentHandleTimeChange = null;
    item.avgAgentHandleTimeChangeWithoutAbs = noneDataSorting;
    item.avgAgentHandleTimeChangeDisplay = null;
  }

  // avg response time
  item.avgAgentResponseTimeDisplay = WyUtils.toNiceTimeFormat(item.avgAgentResponseTime * 60);
  if (item.avgAgentResponseTimeCompare) {
    item.avgAgentResponseTimeCompareDisplay = WyUtils.toNiceTimeFormat(item.avgAgentResponseTimeCompare * 60);
    item.avgAgentResponseTimeChangeWithoutAbs = (item.avgAgentResponseTime - item.avgAgentResponseTimeCompare) / item.avgAgentResponseTimeCompare;
    item.avgAgentResponseTimeChange = Math.abs(item.avgAgentResponseTimeChangeWithoutAbs);
    item.avgAgentResponseTimeChangeDisplay = convertToChangeValue(item.avgAgentResponseTimeChangeWithoutAbs) + (item.avgAgentResponseTimeChange * 100).toFixed(2);
    item.avgAgentResponseTimeChangePercent = (item.avgAgentResponseTimeChange * 100).toFixed(2);
    item.avgAgentResponseTimeChangeTooltip = generatTooltipsMsg(item.avgAgentResponseTimeCompareDisplay, item.numOfDaysInRange, false);
    item.avgAgentResponseTimeChangeWithoutAbsNum = parseFloat(item.avgAgentResponseTimeChangeWithoutAbs);
  } else {
    item.avgAgentResponseTimeCompareDisplay = null;
    item.avgAgentResponseTimeChange = null;
    item.avgAgentResponseTimeChangeWithoutAbs = noneDataSorting;
    item.avgAgentResponseTimeChangeWithoutAbsNum = noneDataSorting;
    item.avgAgentResponseTimeChangeDisplay = null;
  }

  // median response time
  item.medianResponseTimeDisplay = WyUtils.toNiceTimeFormat(item.medianResponseTime * 60);
  if (item.medianResponseTimeCompare) {
    item.medianResponseTimeCompareDisplay = WyUtils.toNiceTimeFormat(item.medianResponseTimeCompare * 60);
    item.medianResponseTimeChangeWithoutAbs = (item.medianResponseTime - item.medianResponseTimeCompare) / item.medianResponseTimeCompare;
    item.medianResponseTimeChange = Math.abs(item.medianResponseTimeChangeWithoutAbs);
    item.medianResponseTimeChangeDisplay = convertToChangeValue(item.medianResponseTimeChangeWithoutAbs) + (item.medianResponseTimeChange * 100).toFixed(2);
    item.medianResponseTimeChangeTooltip = generatTooltipsMsg(item.medianResponseTimeCompare, item.numOfDaysInRange, false);
  } else {
    item.medianResponseTimeCompareDisplay = null;
    item.medianResponseTimeChange = null;
    item.medianResponseTimeChangeWithoutAbs = noneDataSorting;
    item.medianResponseTimeChangeDisplay = null;
  }
}

function generatAlertIconData(item) {
  item.alertsIcon = {
    icon: '',
    value: 0,
  };
  // item.dateFirstFound = '2020-11-10';
  if (item.dateFirstFound && item.dateFirstFound != '') {
    item.dateFirstFoundSorting = moment(item.dateFirstFound, 'YYYY-MM-DD').format('X');
    item.dateFirstFound = WyUtils.formatDate(item.dateFirstFound);
  }
}

function generatTooltipsMsg(compareValue, numOfDaysInRange, isPercentage) {
  let message = '';
  let percentage = '';
  if (isPercentage) {
    percentage = '%';
  }
  message = `Compared to ${compareValue + percentage} over the previous ${numOfDaysInRange} days.`;
  return message;
}

export default {
  state,
  getters,
  mutations,
  actions,
};
